import type { AttendanceDetailRow } from './types'

/*
 * Writing CSV, which this app has only ever read.
 *
 * `accommodation-csv.ts` parses; nothing here produced a file until now. The rules that
 * matter are the ones real data breaks: a venue in this database is called
 * "ILD , UiTM Bandar Enstek" and a session is called "Skim Perkhidmatan Pelajar, Tabung
 * Kecemasan, e-Medical Report", so anything that joins cells with commas and hopes is
 * already wrong on row one.
 */

/** RFC 4180: quote when the value contains a delimiter, a quote or a line break. */
function cell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  const text = String(value)
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/*
 * CRLF, not LF. Excel accepts both, but plenty of older tooling on a university desktop
 * does not, and this file exists to be opened by whatever the organiser has.
 */
function rows(lines: Array<Array<string | number | null | undefined>>): string {
  return lines.map((line) => line.map(cell).join(',')).join('\r\n')
}

const HEADERS = [
  'Session ID', 'Session', 'Venue', 'Date', 'Time',
  'Name', 'Email', 'Track', 'Checked in at',
] as const

/**
 * One row per person per session, ordered by session then name — the shape asked for:
 * per slot, with the detail of who attended it.
 *
 * The same builder serves one session and the whole event; the caller decides by what
 * it passes in, so a single-session export and an all-sessions export cannot drift.
 */
export function attendanceCsv(detail: AttendanceDetailRow[]): string {
  return rows([
    [...HEADERS],
    ...detail.map((r) => [
      r.id_slot, r.slot_name, r.venue_name, r.slot_date, r.slot_time,
      r.name, r.email, r.track_name, r.checked_in_at,
    ]),
  ])
}

/*
 * The BOM goes in the Blob, not in the string.
 *
 * Excel reads a UTF-8 CSV as the local codepage unless the file opens with one, which is
 * how "Nur Aisyah" becomes mojibake on a Malaysian desktop. Keeping it out of the string
 * means the value this module returns is still clean CSV for anything that wants to test
 * or re-parse it.
 */
export function csvBlob(csv: string): Blob {
  return new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' })
}

/** Safe for a filename on Windows, and still readable. */
export function csvFilename(label: string, date = new Date()): string {
  const stamp = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kuala_Lumpur', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date)
  const safe = label.replace(/[\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60)
  return `${safe || 'attendance'} ${stamp}.csv`
}

/**
 * Hand the file to the browser and clean up after.
 *
 * The object URL is revoked on a timeout rather than immediately, because revoking
 * before the click has been dispatched cancels the download in some browsers. A
 * timeout and not `requestAnimationFrame`: rAF does not run in a backgrounded tab, and
 * measuring showed the revoke simply never firing there - the URL then holds its blob
 * until the page unloads.
 */
export function downloadCsv(csv: string, filename: string): void {
  const url = URL.createObjectURL(csvBlob(csv))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
